// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { formatDate, formatCurrency, formatDateTime } from '@/lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  CheckCircle2, 
  CreditCard, 
  FileText, 
  Loader2, 
  MoreHorizontal, 
  RefreshCw, 
  Trash2,
  ClipboardList,
  Receipt,
  Plus,
  Minus,
  AlertTriangle,
  CalendarIcon,
  CheckCircle,
  XCircle,
  Clock,
  Info,
  Package,
  LayoutGrid,
  Building,
  DollarSign,
  Star,
  Activity,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/components/ui/use-toast";
import { StatusBadge } from '@/components/common/StatusBadge';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface SubscriptionItemDetail {
  id: string;
  subscription_id: string;
  subscription_plan_id: string;
  quantity: number;
  unit_price: number;
  state: string | null;
  current_term_start: string | null;
  current_term_end: string | null;
  currency_code: string | null;
  next_billing_at: string | null;
  amount_price: number | null;
  canceled_at: string | null;
  cancellation_reason: string | null;
  billing_start_terms: string | null;
  billing_cycles: number | null;
  term_start: string | null;
  term_end: string | null;
  billing_by: string | null;
  auto_renew: boolean | null;
  created_at: string;
  updated_at?: string;
  subscription: {
    id: string;
    state: string;
    customer_relationship: {
      id: string;
      relationship?: string;
      customer: {
        id: string;
        name: string;
        legal_name: string;
        domain?: string;
      }
    } | null;
  };
  subscription_plan: {
    id: string;
    name: string;
    description: string | null;
    billing_frequency: string;
    sku: string | null;
    family_id: string | null;
    family: {
      id: string;
      name: string;
    } | null;
    prices: {
      id: string;
      currency: string;
      price_amount: number;
      cost_amount: number | null;
    }[];
    billing_period_type: string | null;
    billing_period_duration: string | null;
  };
  orders: {
    id: string;
    subscription_id?: string;
    operation_type: string;
    quantity_change: number | null;
    operation_date: string;
    created_at: string;
    notes?: string;
    subscription_item?: {
      subscription_plan?: {
        name: string;
      }
    };
  }[];
  invoice_items: {
    id: string;
    invoice_number: string;
    invoice_date: string;
    amount_due: number;
    currency: string;
    due_date: string | null;
    paid_date: string | null;
    status: string;
  }[];
}

const SubscriptionItemDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptionItem, setSubscriptionItem] = useState<SubscriptionItemDetail | null>(null);
  const [operations, setOperations] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isQuantityChangeDialogOpen, setIsQuantityChangeDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [quantityChangeType, setQuantityChangeType] = useState<'aumento' | 'redução'>('aumento');
  const [originalQuantity, setOriginalQuantity] = useState(0);
  const [newQuantity, setNewQuantity] = useState(0);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [invoiceDate, setInvoiceDate] = useState<Date | undefined>(new Date());
  const [invoiceDueDate, setInvoiceDueDate] = useState<Date | undefined>(
    new Date(new Date().setDate(new Date().getDate() + 30))
  );
  const { toast } = useToast();

  useEffect(() => {
    if (id) {
      fetchSubscriptionItemDetails(id);
    } else {
      setLoading(false);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'ID do item da assinatura não fornecido'
      });
    }
  }, [id]);

  const fetchSubscriptionItemDetails = async (itemId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscription_items')
        .select(`
          *,
          subscription:subscription_id (
            id,
            state,
            customer_relationship:customer_relationship_id (
              id,
              relationship,
              customer:customer_id (
                id,
                name,
                legal_name,
                domain
              )
            )
          ),
          subscription_plan:subscription_plan_id (
            id,
            name,
            description,
            billing_frequency,
            sku,
            family_id,
            family:family_id (
              id,
              name
            ),
            prices:subscription_plan_prices (
              id,
              currency,
              price_amount,
              cost_amount
            ),
            billing_period_type,
            billing_period_duration
          )
        `)
        .eq('id', itemId)
        .single();

      if (error) throw error;
      
      const typedData = data as SubscriptionItemDetail;
      
      const subscriptionItemWithArrays: SubscriptionItemDetail = {
        ...typedData,
        orders: [],
        invoice_items: []
      };
      
      setSubscriptionItem(subscriptionItemWithArrays);
      setOriginalQuantity(data.quantity);
      setNewQuantity(data.quantity);
      setCancellationReason('');
      
      await refreshOrders(subscriptionItemWithArrays);
      await refreshInvoices(subscriptionItemWithArrays);
    } catch (error) {
      console.error('Erro ao buscar detalhes do item:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: 'Não foi possível carregar os detalhes do item'
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshOrders = async (currentItem = subscriptionItem) => {
    if (!currentItem || !id) return;
    
    setLoading(true);
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`*`)
        .eq('subscription_id', currentItem.subscription_id)
        .order('operation_date', { ascending: false });

      if (ordersError) throw ordersError;

      const relevantOrders = (ordersData || []).filter(order => {
        return order.subscription_plan_id === currentItem.subscription_plan_id || 
               !order.subscription_plan_id;
      });
      
      const formattedOrders = relevantOrders.map(order => ({
        ...order,
        notes: order.notes || '',
        subscription_item: {
          subscription_plan: { name: "Current Plan" }
        }
      }));
      
      setSubscriptionItem(prev => prev ? { ...prev, orders: formattedOrders } : null);
    } catch (error) {
      console.error('Erro ao buscar ordens:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível carregar as ordens' });
    } finally {
      setLoading(false);
    }
  };

  const refreshInvoices = async (currentItem = subscriptionItem) => {
    if (!id || !currentItem) return;
    
    setLoading(true);
    try {
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select(`*`)
        .eq('subscription_id', currentItem.subscription_id);

      if (invoicesError) throw invoicesError;
      
      const invoicesWithRelevantItems = [];
      for (const invoice of invoicesData || []) {
        const { data: invoiceItems, error: itemsError } = await supabase
          .from('invoice_items')
          .select('*')
          .eq('invoice_id', invoice.id)
          .eq('subscription_plan_id', currentItem.subscription_plan_id);
          
        if (itemsError) {
          console.error(`Error checking items for invoice ${invoice.id}:`, itemsError);
          continue;
        }
        
        if (invoiceItems && invoiceItems.length > 0) {
          invoicesWithRelevantItems.push(invoice);
        }
      }
      
      setSubscriptionItem(prev => prev ? { ...prev, invoice_items: invoicesWithRelevantItems } : null);
    } catch (error) {
      console.error('Erro ao buscar faturas:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível carregar as faturas' });
    } finally {
      setLoading(false);
    }
  };


  const getOperationTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'criação': 'Criação', 'aumento': 'Aumento', 'redução': 'Redução',
      'cancelamento': 'Cancelamento', 'renovação': 'Renovação', 'faturamento': 'Faturamento'
    };
    return types[type.toLowerCase()] || type;
  };

  const getBillingPeriodLabel = (periodType: string | null | undefined, duration: string | number | null | undefined) => {
    if (!periodType || !duration) return '-';
    const durNum = typeof duration === 'string' ? parseInt(duration) : duration;
    if (isNaN(durNum)) return '-';

    const labels: Record<string, string[]> = {
      'month': ['Mês', 'Meses'], 'year': ['Ano', 'Anos'], 'day': ['Dia', 'Dias'], 'week': ['Semana', 'Semanas']
    };

    const periodLabel = labels[periodType.toLowerCase()];
    if(periodLabel) {
      return durNum === 1 ? periodLabel[0] : `${durNum} ${periodLabel[1]}`;
    }
    return `${durNum} ${periodType}`;
  };

  const handleQuantityChange = async () => {
    if (!subscriptionItem || !id) return;
    setLoading(true);
    try {
      const changeValue = quantityChangeType === 'aumento' ? originalQuantity : -originalQuantity;
      const newQuantityValue = subscriptionItem.quantity + changeValue;
      
      if (newQuantityValue <= 0) {
        toast({ variant: "destructive", title: "Erro", description: 'A quantidade não pode ser menor ou igual a zero' });
        setLoading(false);
        return;
      }
      
      const { error: updateError } = await supabase.from('subscription_items').update({ quantity: newQuantityValue }).eq('id', id);
      if (updateError) throw updateError;
      
      const { error: orderError } = await supabase.from('orders').insert({
        subscription_id: subscriptionItem.subscription_id,
        operation_type: quantityChangeType,
        quantity_change: changeValue,
        operation_date: new Date().toISOString(),
      });
      if (orderError) throw orderError;
      
      await fetchSubscriptionItemDetails(id); // Refetch all data to ensure consistency
      
      toast({ title: "Sucesso", description: `Quantidade alterada com sucesso` });
      setIsQuantityChangeDialogOpen(false);
    } catch (error) {
      console.error('Erro ao alterar quantidade:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível alterar a quantidade: ' + (error as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const openQuantityChangeDialog = (type: 'aumento' | 'redução') => {
    setQuantityChangeType(type);
    setOriginalQuantity(1); // Reset to 1 on open
    setIsQuantityChangeDialogOpen(true);
  };

  const handleCancelItem = async () => {
    if (!subscriptionItem || !id) return;
    if (!cancelReason) {
      toast({ variant: "destructive", title: "Erro", description: 'Por favor, selecione um motivo para o cancelamento' });
      return;
    }
    
    setLoading(true);
    try {
      const formattedReason = `${cancelReason}${cancellationReason ? `: ${cancellationReason}` : ''}`;
      
      const { error: updateError } = await supabase.from('subscription_items').update({ 
        state: 'canceled', canceled_at: new Date().toISOString(), cancellation_reason: formattedReason
      }).eq('id', id);
      if (updateError) throw updateError;
      
      const { error: orderError } = await supabase.from('orders').insert({
        subscription_id: subscriptionItem.subscription_id, subscription_item_id: id,
        subscription_plan_id: subscriptionItem.subscription_plan_id, operation_type: 'cancelamento',
        operation_date: new Date().toISOString(), notes: `Item cancelado: ${formattedReason}`
      });
      if (orderError) throw orderError;
      
      const { data: activeItems } = await supabase.from('subscription_items').select('id')
        .eq('subscription_id', subscriptionItem.subscription_id).eq('state', 'active').neq('id', id);
      
      if (!activeItems || activeItems.length === 0) {
        const { error: subscriptionError } = await supabase.from('subscriptions').update({
          state: 'canceled', canceled_at: new Date().toISOString(), cancellation_reason: `Cancelamento automático: ${formattedReason}`
        }).eq('id', subscriptionItem.subscription_id);
        
        if (subscriptionError) {
          toast({ variant: "destructive", title: "Atenção", description: 'Item cancelado, mas não foi possível cancelar a assinatura automaticamente.' });
        } else {
          await supabase.from('orders').insert({
            subscription_id: subscriptionItem.subscription_id, operation_type: 'cancelamento',
            operation_date: new Date().toISOString(), notes: `Assinatura cancelada automaticamente. Motivo: ${formattedReason}`
          });
          toast({ title: "Sucesso", description: 'Item e assinatura cancelados com sucesso.' });
        }
      } else {
        toast({ title: "Sucesso", description: 'Item cancelado com sucesso' });
      }
      
      await fetchSubscriptionItemDetails(id);
      setIsCancelDialogOpen(false);
    } catch (error) {
      console.error('Erro ao cancelar item:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível cancelar o item: ' + (error as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const openCancelDialog = () => {
    setCancelReason('');
    setCancellationReason('');
    setIsCancelDialogOpen(true);
  };

  const generateInvoiceNumber = () => {
    const firstPart = Math.floor(100000 + Math.random() * 900000).toString();
    const secondPart = Math.floor(100 + Math.random() * 900).toString();
    return `${firstPart}-${secondPart}`;
  };

  const handleGenerateInvoice = async () => {
    if (!subscriptionItem || !id) return;
    if (!invoiceDate || !invoiceDueDate || !invoiceAmount) {
      toast({ variant: "destructive", title: "Erro", description: 'Por favor, preencha todos os campos obrigatórios' });
      return;
    }
    
    setLoading(true);
    try {
      const { data: activeItems, error: itemsError } = await supabase.from('subscription_items')
        .select(`id, quantity, unit_price, subscription_plan_id`).eq('subscription_id', subscriptionItem.subscription_id).neq('state', 'canceled');
      if (itemsError) throw itemsError;
      if (!activeItems || activeItems.length === 0) {
        toast({ variant: "destructive", title: "Atenção", description: 'Não há itens ativos nesta assinatura' });
        return;
      }
      
      const totalAmount = activeItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      
      const { data: invoice, error: invoiceError } = await supabase.from('invoices').insert({
        subscription_id: subscriptionItem.subscription_id, invoice_number: generateInvoiceNumber(),
        invoice_date: invoiceDate.toISOString(), amount_due: invoiceAmount,
        currency: subscriptionItem.currency_code || 'BRL', due_date: invoiceDueDate.toISOString(),
        status: 'pending', created_by: 'manual', updated_by: 'manual'
      }).select().single();
      if (invoiceError) throw invoiceError;
      
      const invoiceItemsToInsert = activeItems.map(item => ({
        invoice_id: invoice.id, subscription_plan_id: item.subscription_plan_id,
        quantity: item.quantity, unit_price: item.unit_price,
        currency_code: subscriptionItem.currency_code || 'BRL',
        amount_price: (item.quantity * item.unit_price / totalAmount) * invoiceAmount,
        created_by: 'manual', updated_by: 'manual'
      }));
      
      const { error: invoiceItemsError } = await supabase.from('invoice_items').insert(invoiceItemsToInsert);
      if (invoiceItemsError) throw invoiceItemsError;
      
      const { error: orderError } = await supabase.from('orders').insert({
        subscription_id: subscriptionItem.subscription_id, operation_type: 'faturamento',
        operation_date: new Date().toISOString(),
      });
      if (orderError) throw orderError;
      
      await refreshInvoices();
      toast({ title: "Sucesso", description: 'Fatura gerada com sucesso' });
      setIsInvoiceDialogOpen(false);
    } catch (error) {
      console.error('Erro ao gerar fatura:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível gerar a fatura: ' + (error as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const openInvoiceDialog = async () => {
    if (!subscriptionItem) return;
    try {
      const { data: activeItems, error: itemsError } = await supabase.from('subscription_items')
        .select(`id, quantity, unit_price, subscription_plan:subscription_plan_id(name)`).eq('subscription_id', subscriptionItem.subscription_id).neq('state', 'canceled');
      if (itemsError) throw itemsError;
      if (!activeItems || activeItems.length === 0) {
        toast({ variant: "destructive", title: "Atenção", description: 'Não há itens ativos nesta assinatura para faturar' });
        return;
      }
      
      const totalAmount = activeItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      setInvoiceDate(new Date());
      setInvoiceDueDate(new Date(new Date().setDate(new Date().getDate() + 30)));
      setInvoiceAmount(totalAmount);
      setInvoices(activeItems);
      setIsInvoiceDialogOpen(true);
    } catch (error) {
      console.error('Erro ao preparar geração de fatura:', error);
      toast({ variant: "destructive", title: "Erro", description: 'Não foi possível preparar a geração de fatura' });
    }
  };

  // Função para obter gradiente baseado no status
  const getStatusGradient = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'from-green-500 via-green-600 to-green-700';
      case 'canceled':
        return 'from-red-500 via-red-600 to-red-700';
      case 'paused':
        return 'from-yellow-500 via-yellow-600 to-yellow-700';
      case 'expired':
        return 'from-orange-500 via-orange-600 to-orange-700';
      default:
        return 'from-[#3b65f5] via-[#ff4a8f] to-[#c8e600]';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-[#1F1746] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <Loader2 className="h-16 w-16 animate-spin mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Carregando Item</h1>
              <p className="text-white/80">Aguarde enquanto buscamos os detalhes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionItem) {
    return (
      <div className="min-h-screen bg-[#FCFCFD]">
        {/* Hero Section with Gradient Background */}
        <div className="relative bg-[#1F1746] overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative z-10 container mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center text-center text-white">
              <AlertCircle className="h-16 w-16 mb-6" />
              <h1 className="text-2xl font-extrabold mb-2">Item Não Encontrado</h1>
              <p className="text-white/80 mb-6">O item da assinatura não foi encontrado em nossos registros.</p>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/subscriptions')}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar para Assinaturas
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const customer = subscriptionItem.subscription?.customer_relationship?.customer;

  return (
    <div className="min-h-screen bg-[#FCFCFD]">
      {/* Hero Section with Dynamic Gradient Based on Status */}
      <div className="relative bg-[#1F1746] overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full transform translate-x-48 -translate-y-48" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full transform -translate-x-32 translate-y-32" />
        
        <div className="relative z-10 container mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-white">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate(`/subscriptions/${subscriptionItem.subscription_id}`)}
                  className="text-white hover:bg-white/20 border-white/30"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="h-8 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <Package className="h-6 w-6" />
                  <span className="text-white/80">Item da Assinatura</span>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-extrabold mb-3">
                {subscriptionItem.subscription_plan.name}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-white/90">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  <span>{customer?.name || 'Cliente não identificado'}</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-bold">{formatCurrency(subscriptionItem.quantity * subscriptionItem.unit_price, subscriptionItem.currency_code || 'BRL')}</span>
                </div>
                <div className="h-4 w-px bg-white/30" />
                <StatusBadge 
                  status={subscriptionItem.state} 
                  context="subscription" 
                  className="bg-white/20 text-white border-white/30"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  >
                    <MoreHorizontal className="h-4 w-4 mr-2" />
                    Ações
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Gerenciar Item</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => openQuantityChangeDialog('aumento')} disabled={subscriptionItem.state === 'canceled'}>
                    <Plus className="h-4 w-4 mr-2" />
                    Aumentar Quantidade
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openQuantityChangeDialog('redução')} disabled={subscriptionItem.state === 'canceled' || subscriptionItem.quantity <= 1}>
                    <Minus className="h-4 w-4 mr-2" />
                    Reduzir Quantidade
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openInvoiceDialog} disabled={subscriptionItem.state === 'canceled' || subscriptionItem.subscription?.state !== 'active'}>
                    <FileText className="h-4 w-4 mr-2" />
                    Gerar Fatura
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={openCancelDialog} className="text-red-600" disabled={subscriptionItem.state === 'canceled'}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Cancelar Item
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                onClick={() => navigate(`/subscriptions/${subscriptionItem.subscription_id}`)}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ver Assinatura
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10 space-y-8">
        
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Status Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded-md">
                    <Activity className="h-4 w-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Status</span>
                </div>
                <StatusBadge status={subscriptionItem.state} context="subscription" />
              </div>
              <div className="text-xs text-gray-500">
                Qtd: {subscriptionItem.quantity} unidade{subscriptionItem.quantity !== 1 ? 's' : ''}
              </div>
            </CardContent>
          </Card>

          {/* Amount Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <DollarSign className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Valor Total</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {formatCurrency(subscriptionItem.quantity * subscriptionItem.unit_price, subscriptionItem.currency_code || 'BRL')}
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {formatCurrency(subscriptionItem.unit_price, subscriptionItem.currency_code || 'BRL')} /unidade
              </div>
            </CardContent>
          </Card>

          {/* Next Billing Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-orange-100 rounded-md">
                    <Calendar className="h-4 w-4 text-orange-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Próx. Cobrança</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-extrabold text-gray-900">
                    {subscriptionItem.next_billing_at ? formatDate(subscriptionItem.next_billing_at) : 'N/A'}
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Data de faturamento
              </div>
            </CardContent>
          </Card>

          {/* Current Term Card */}
          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    <Clock className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Termo Vigente</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-extrabold text-gray-900">
                    {subscriptionItem.current_term_start ? 
                      `${formatDate(subscriptionItem.current_term_start)} - ${subscriptionItem.current_term_end ? formatDate(subscriptionItem.current_term_end) : '...'}` : 
                      'N/A'
                    }
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Período atual
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Main Content */}
        <Card className="border shadow-sm bg-white/90 backdrop-blur-sm">
          <CardContent className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex bg-gray-100 rounded-lg p-1 mb-6 max-w-fit">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'overview'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <LayoutGrid className="h-4 w-4" />
                  Visão Geral
                </button>
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'orders'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <ClipboardList className="h-4 w-4" />
                  Operações
                </button>
                <button
                  onClick={() => setActiveTab('invoices')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'invoices'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Receipt className="h-4 w-4" />
                  Faturas
                </button>
              </div>

              <TabsContent value="overview" className="mt-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Plan Information */}
                  <Card className="lg:col-span-1 border shadow-sm bg-gradient-to-br from-white to-gray-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Package className="h-5 w-5 text-blue-600" />
                        Informações do Plano
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">Plano</h3>
                        <div className="space-y-1">
                          <div className="font-extrabold text-gray-900">
                            {subscriptionItem.subscription_plan.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {subscriptionItem.subscription_plan.description || 'Sem descrição'}
                          </div>
                        </div>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">Família</h3>
                        <div className="text-sm text-gray-900">
                          {subscriptionItem.subscription_plan.family?.name || 'N/A'}
                        </div>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">SKU</h3>
                        <div className="text-sm text-gray-900 font-mono">
                          {subscriptionItem.subscription_plan.sku || 'N/A'}
                        </div>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-2">Preço Base</h3>
                        <div className="text-sm text-gray-900 font-extrabold">
                          {formatCurrency(subscriptionItem.subscription_plan.prices?.[0]?.price_amount || 0, subscriptionItem.subscription_plan.prices?.[0]?.currency || 'BRL')}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Item Details */}
                  <Card className="lg:col-span-2 border shadow-sm bg-gradient-to-br from-white to-gray-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Star className="h-5 w-5 text-green-600" />
                        Detalhes do Item
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-sm font-extrabold text-gray-600 mb-3">Configuração</h3>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Quantidade</span>
                              <span className="text-sm font-extrabold">{subscriptionItem.quantity}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Preço Unitário</span>
                              <span className="text-sm font-extrabold">{formatCurrency(subscriptionItem.unit_price, subscriptionItem.currency_code || 'BRL')}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Valor Total</span>
                              <span className="text-lg font-extrabold text-green-600">{formatCurrency(subscriptionItem.quantity * subscriptionItem.unit_price, subscriptionItem.currency_code || 'BRL')}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h3 className="text-sm font-extrabold text-gray-600 mb-3">Cronologia</h3>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-500">Próx. Cobrança</span>
                              <span className="text-sm font-extrabold">{subscriptionItem.next_billing_at ? formatDate(subscriptionItem.next_billing_at) : 'N/A'}</span>
                            </div>
                            {subscriptionItem.canceled_at && (
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">Cancelado em</span>
                                <span className="text-sm font-extrabold text-red-600">{formatDate(subscriptionItem.canceled_at)}</span>
                              </div>
                            )}
                            {subscriptionItem.cancellation_reason && (
                              <div>
                                <span className="text-sm text-gray-500">Motivo</span>
                                <div className="text-sm text-gray-900 mt-1 p-2 bg-red-50 rounded border border-red-200">
                                  {subscriptionItem.cancellation_reason}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <h3 className="text-sm font-extrabold text-gray-600 mb-3">Assinatura Relacionada</h3>
                        <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div>
                            <div className="font-extrabold text-gray-900">#{subscriptionItem.subscription_id.substring(0, 8)}</div>
                            <div className="text-sm text-gray-500">Cliente: {customer?.name || 'Não encontrado'}</div>
                            <div className="text-sm text-gray-500">Status: <StatusBadge status={subscriptionItem.subscription?.state} context="subscription" /></div>
                          </div>
                          <Button variant="outline" onClick={() => navigate(`/subscriptions/${subscriptionItem.subscription_id}`)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Ver Assinatura
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="orders">
                <Card><CardHeader><div className="flex justify-between items-center"><CardTitle>Operações</CardTitle><Button variant="outline" onClick={() => refreshOrders()}><RefreshCw className="h-4 w-4 mr-2" />Atualizar</Button></div><CardDescription>Histórico de operações realizadas neste item</CardDescription></CardHeader><CardContent>{loading ? <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : subscriptionItem.orders && subscriptionItem.orders.length > 0 ? <Table><TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Tipo</TableHead><TableHead>Alteração Qtd.</TableHead><TableHead>Data de Criação</TableHead></TableRow></TableHeader><TableBody>{subscriptionItem.orders.map((order) => (<TableRow key={order.id}><TableCell>{formatDate(order.operation_date)}</TableCell><TableCell><Badge variant={order.operation_type.includes('aumento') || order.operation_type.includes('criação') ? 'default' : 'destructive'}>{getOperationTypeLabel(order.operation_type)}</Badge></TableCell><TableCell><span className={order.quantity_change > 0 ? 'text-green-600' : 'text-red-600'}>{order.quantity_change !== null ? (order.quantity_change > 0 ? '+' : '') + order.quantity_change : '-'}</span></TableCell><TableCell>{formatDateTime(order.created_at)}</TableCell></TableRow>))}</TableBody></Table> : <div className="text-center py-8 text-muted-foreground">Nenhuma operação encontrada.</div>}</CardContent></Card>
              </TabsContent>

              <TabsContent value="invoices">
                <Card><CardHeader><div className="flex justify-between items-center"><CardTitle>Faturas</CardTitle><Button variant="outline" onClick={() => refreshInvoices()}><RefreshCw className="h-4 w-4 mr-2" />Atualizar</Button></div><CardDescription>Histórico de faturas deste item</CardDescription></CardHeader><CardContent>{loading ? <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> : subscriptionItem.invoice_items && subscriptionItem.invoice_items.length > 0 ? <Table><TableHeader><TableRow><TableHead>Número</TableHead><TableHead>Data</TableHead><TableHead>Valor</TableHead><TableHead>Vencimento</TableHead><TableHead>Pagamento</TableHead><TableHead>Status</TableHead></TableRow></TableHeader><TableBody>{subscriptionItem.invoice_items.map((invoice) => (<TableRow key={invoice.id}><TableCell className="font-extrabold">{invoice.invoice_number}</TableCell><TableCell>{formatDate(invoice.invoice_date)}</TableCell><TableCell>{formatCurrency(invoice.amount_due, invoice.currency)}</TableCell><TableCell>{invoice.due_date ? formatDate(invoice.due_date) : '-'}</TableCell><TableCell>{invoice.paid_date ? formatDate(invoice.paid_date) : '-'}</TableCell><TableCell><StatusBadge status={invoice.status} context="invoice" /></TableCell></TableRow>))}</TableBody></Table> : <div className="text-center py-8 text-muted-foreground">Nenhuma fatura encontrada.</div>}</CardContent></Card>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <Dialog open={isQuantityChangeDialogOpen} onOpenChange={setIsQuantityChangeDialogOpen}><DialogContent>
        <DialogHeader><DialogTitle>{quantityChangeType === 'aumento' ? 'Aumentar' : 'Reduzir'} Quantidade</DialogTitle><DialogDescription>Informe a quantidade para {quantityChangeType === 'aumento' ? 'adicionar ao' : 'reduzir do'} plano.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Quantidade atual</Label><Input value={subscriptionItem?.quantity || 0} disabled /></div>
            <div className="space-y-2"><Label>Quantidade a {quantityChangeType === 'aumento' ? 'adicionar' : 'reduzir'}</Label><Input type="number" min={1} max={quantityChangeType === 'redução' ? ((subscriptionItem?.quantity || 1) - 1) : undefined} value={originalQuantity} onChange={(e) => setOriginalQuantity(Math.max(1, parseInt(e.target.value) || 1))} /></div>
            <div className="space-y-2"><Label>Nova quantidade</Label><Input value={quantityChangeType === 'aumento' ? (subscriptionItem?.quantity || 0) + originalQuantity : (subscriptionItem?.quantity || 0) - originalQuantity} disabled /></div>
            <div className="space-y-2"><Label>Novo valor total</Label><Input value={formatCurrency(((quantityChangeType === 'aumento' ? (subscriptionItem.quantity + originalQuantity) : (subscriptionItem.quantity - originalQuantity)) * subscriptionItem.unit_price), subscriptionItem.currency_code || 'BRL')} disabled /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setIsQuantityChangeDialogOpen(false)} disabled={loading}>Cancelar</Button><Button onClick={handleQuantityChange} disabled={loading || originalQuantity < 1 || (quantityChangeType === 'redução' && originalQuantity >= (subscriptionItem?.quantity || 1))}>{loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processando</> : 'Confirmar'}</Button></DialogFooter>
      </DialogContent></Dialog>
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}><DialogContent>
        <DialogHeader><DialogTitle className="text-destructive flex items-center gap-2"><Trash2 className="h-5 w-5" />Cancelar Item de Assinatura</DialogTitle><DialogDescription>Esta ação não pode ser desfeita.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-4">
            <div className="space-y-2"><Label htmlFor="cancel-reason">Motivo do Cancelamento</Label><Select value={cancelReason} onValueChange={setCancelReason}><SelectTrigger><SelectValue placeholder="Selecione um motivo" /></SelectTrigger><SelectContent><SelectItem value="Solicitação do cliente">Solicitação do cliente</SelectItem><SelectItem value="Inadimplência">Inadimplência</SelectItem><SelectItem value="Migração de plano">Migração de plano</SelectItem><SelectItem value="Insatisfação com o serviço">Insatisfação com o serviço</SelectItem><SelectItem value="Erro operacional">Erro operacional</SelectItem><SelectItem value="Outro">Outro</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label htmlFor="cancel-details">Detalhes (opcional)</Label><Textarea placeholder="Forneça mais detalhes..." value={cancellationReason} onChange={(e) => setCancellationReason(e.target.value)} rows={3} /></div>
            <div className="bg-amber-50 p-4 rounded-md border border-amber-200"><div className="flex items-start gap-2"><AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" /><div><h4 className="font-extrabold text-amber-800">Atenção</h4><p className="text-sm text-amber-700 mt-1">O cancelamento deste item irá interromper a cobrança e, se for o último item ativo, cancelar a assinatura inteira.</p></div></div></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setIsCancelDialogOpen(false)} disabled={loading}>Voltar</Button><Button variant="destructive" onClick={handleCancelItem} disabled={loading || !cancelReason}>{loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processando</> : 'Confirmar Cancelamento'}</Button></DialogFooter>
      </DialogContent></Dialog>
      <Dialog open={isInvoiceDialogOpen} onOpenChange={setIsInvoiceDialogOpen}><DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Gerar Nova Fatura</DialogTitle><DialogDescription>A fatura incluirá todos os itens ativos da assinatura.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Data da Fatura</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{invoiceDate ? format(invoiceDate, "dd/MM/yyyy", { locale: ptBR }) : <span>Selecione</span>}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><CalendarComponent mode="single" selected={invoiceDate} onSelect={(date) => date && setInvoiceDate(date)} initialFocus locale={ptBR} /></PopoverContent></Popover></div>
                <div className="space-y-2"><Label>Data de Vencimento</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{invoiceDueDate ? format(invoiceDueDate, "dd/MM/yyyy", { locale: ptBR }) : <span>Selecione</span>}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><CalendarComponent mode="single" selected={invoiceDueDate} onSelect={setInvoiceDueDate} initialFocus locale={ptBR} disabled={(date) => date < new Date()} /></PopoverContent></Popover></div>
            </div>
            <div className="space-y-2"><Label htmlFor="invoice-amount">Valor Total</Label><div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span><Input id="invoice-amount" type="number" value={invoiceAmount} onChange={(e) => setInvoiceAmount(parseFloat(e.target.value) || 0)} className="pl-8" placeholder="0,00" step="0.01" min="0" /></div></div>
            <div className="space-y-2"><Label>Itens Incluídos</Label><div className="border rounded-md"><Table><TableHeader><TableRow><TableHead>Plano</TableHead><TableHead className="text-center">Qtd.</TableHead><TableHead>Preço Unit.</TableHead><TableHead className="text-right">Subtotal</TableHead></TableRow></TableHeader><TableBody>{invoices.map((item) => (<TableRow key={item.id}><TableCell className="font-extrabold">{item.subscription_plan?.name || 'N/A'}</TableCell><TableCell className="text-center">{item.quantity}</TableCell><TableCell>{formatCurrency(item.unit_price, subscriptionItem?.currency_code || 'BRL')}</TableCell><TableCell className="text-right">{formatCurrency(item.quantity * item.unit_price, subscriptionItem?.currency_code || 'BRL')}</TableCell></TableRow>))}<TableRow><TableCell colSpan={3} className="text-right font-extrabold">Total:</TableCell><TableCell className="text-right font-extrabold">{formatCurrency(invoices.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0), subscriptionItem?.currency_code || 'BRL')}</TableCell></TableRow></TableBody></Table></div></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setIsInvoiceDialogOpen(false)} disabled={loading}>Cancelar</Button><Button onClick={handleGenerateInvoice} disabled={loading || !invoiceDate || !invoiceDueDate || invoiceAmount === 0}>{loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processando</> : 'Gerar Fatura'}</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
};

export default SubscriptionItemDetail; 